import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Ban, Coins, ClipboardList, PlusCircle, Send, Store, Users } from 'lucide-react';
import {
  ActionNotice,
  ConfirmDialog,
  DataTable,
  FormField,
  FormHelp,
  ModalFrame,
  PageHeading,
  QueryPanel,
  StatusBadge,
  errorMessageOf,
  type DataColumn,
} from '../../components/ui';
import {
  LOW_STORE_OC_THRESHOLD,
  OC_REQUEST_STATUS_LABEL,
  OC_REQUEST_STATUS_TONE,
  OC_REQUEST_TYPE_LABEL,
  isLowStoreOc,
  ocTransactionTypeLabel,
} from '../../lib/oc';
import { formatCurrency, formatDateTime, formatInteger, formatOc } from '../system-admin/data';
import {
  useAllocateOc,
  useCancelOcRequest,
  useFranchiseDashboard,
  useFranchiseOc,
  useFranchiseStores,
  useRequestOc,
} from './hooks';
import { parseOptionalKrw, parsePositiveInteger, previewAllocation, type AllocationMode } from './logic';
import type { FranchiseOcRequest, FranchiseOcTransaction, FranchiseStore } from './types';

function panelHeading(icon: ReactNode, title: string, note?: string) {
  return (
    <div className="panel-heading">
      <span>
        {icon}
        {title}
      </span>
      {note ? <small>{note}</small> : null}
    </div>
  );
}

function requestTarget(row: { store_id: string | null; store_name?: string | null }): string {
  return row.store_id ? (row.store_name ?? '매장') : '본사';
}

function requestColumns(onCancel?: (row: FranchiseOcRequest) => void): DataColumn<FranchiseOcRequest>[] {
  const columns: DataColumn<FranchiseOcRequest>[] = [
    { header: '요청 일시', render: (row) => formatDateTime(row.created_at) },
    { header: '대상', render: (row) => requestTarget(row) },
    { header: '유형', render: (row) => OC_REQUEST_TYPE_LABEL[row.request_type] ?? row.request_type },
    { header: 'OC', align: 'right', render: (row) => formatOc(row.amount) },
    { header: '결제 금액', align: 'right', render: (row) => (row.amount_krw == null ? '-' : formatCurrency(row.amount_krw)) },
    { header: '사유', render: (row) => row.reason },
    {
      header: '상태',
      render: (row) => (
        <>
          <StatusBadge tone={OC_REQUEST_STATUS_TONE[row.status] ?? 'neutral'}>{OC_REQUEST_STATUS_LABEL[row.status] ?? row.status}</StatusBadge>
          {row.review_reason ? <div className="muted-text">{row.review_reason}</div> : null}
        </>
      ),
    },
  ];
  if (onCancel) {
    columns.push({
      header: '작업',
      render: (row) =>
        row.status === 'pending' ? (
          <button className="danger-outline-button compact-button" type="button" onClick={() => onCancel(row)}>
            <Ban size={14} />
            요청 취소
          </button>
        ) : (
          '-'
        ),
    });
  }
  return columns;
}

function transactionColumns(): DataColumn<FranchiseOcTransaction>[] {
  return [
    { header: '일시', render: (row) => formatDateTime(row.created_at) },
    {
      header: '구분',
      render: (row) =>
        row.store_id ? <StatusBadge tone="neutral">{row.store_name ?? '매장'}</StatusBadge> : <StatusBadge tone="success">본사</StatusBadge>,
    },
    { header: '유형', render: (row) => ocTransactionTypeLabel(row.transaction_type) },
    { header: 'OC', align: 'right', render: (row) => `${row.amount > 0 ? '+' : ''}${formatInteger(row.amount)}` },
    { header: '잔액', align: 'right', render: (row) => formatOc(row.balance_after) },
    { header: '메모', render: (row) => row.description ?? '-' },
  ];
}

// ---------------------------------------------------------------- 홈

export function FranchiseDashboardContent() {
  const dashboardQuery = useFranchiseDashboard();
  const storesQuery = useFranchiseStores();

  return (
    <QueryPanel
      data={dashboardQuery.data}
      errorLabel="프랜차이즈 현황을 불러오지 못했습니다."
      isLoading={dashboardQuery.isLoading}
      onRetry={() => void dashboardQuery.refetch()}
    >
      {(data) => {
        const stores = storesQuery.data ?? [];
        const lowStores = stores.filter((store) => isLowStoreOc(store.oc_balance));
        const pendingCount = data.recent_oc_requests.filter((request) => request.status === 'pending').length;
        return (
          <>
            <section className="balance-card" aria-label="본사 OC 잔액">
              <div>
                <span>{data.franchise.name} 본사 OC 잔액</span>
                <strong>{formatOc(data.franchise.oc_balance)}</strong>
                <small>매장에 배분하거나 회수할 수 있는 OC입니다.</small>
              </div>
              <Link className="secondary-button" to="/oc">
                OC 화면으로
                <ArrowRight size={16} />
              </Link>
            </section>

            <section className="stat-grid" aria-label="프랜차이즈 지표">
              <div className="stat-card">
                <Store size={22} />
                <span>매장</span>
                <strong>{formatInteger(data.metrics.store_count)}개</strong>
              </div>
              <div className="stat-card">
                <Coins size={22} />
                <span>매장 OC 합계</span>
                <strong>{formatOc(data.metrics.store_oc_balance)}</strong>
                <small>총 OC(본사 + 매장) {formatOc(data.franchise.oc_balance + data.metrics.store_oc_balance)}</small>
              </div>
              <div className="stat-card">
                <Users size={22} />
                <span>활성 매장 관리자 / 고객</span>
                <strong>
                  {formatInteger(data.metrics.active_store_admin_count)}명 / {formatInteger(data.metrics.active_customer_count)}명
                </strong>
              </div>
              <div className={`stat-card${lowStores.length > 0 ? ' stat-card--alert' : ''}`}>
                <AlertTriangle size={22} />
                <span>OC 낮은 매장</span>
                <strong>{formatInteger(lowStores.length)}개</strong>
                <small>{LOW_STORE_OC_THRESHOLD} OC 이하</small>
              </div>
              <div className="stat-card">
                <Send size={22} />
                <span>대기 중 OC 요청</span>
                <strong>{formatInteger(pendingCount)}건</strong>
                <small>최근 5건 기준</small>
              </div>
            </section>

            <section className="data-panel">
              {panelHeading(<Store size={18} />, '매장별 OC', '낮은 잔액 매장은 강조 표시')}
              <div className="form-panel">
                {storesQuery.isLoading ? (
                  <div className="loading-block">매장 목록을 불러오는 중입니다.</div>
                ) : storesQuery.isError ? (
                  <ActionNotice tone="danger">{errorMessageOf(storesQuery.error, '매장 목록을 불러오지 못했습니다.')}</ActionNotice>
                ) : stores.length === 0 ? (
                  <div className="empty-state">등록된 매장이 없습니다.</div>
                ) : (
                  <div className="store-card-grid">
                    {stores.map((store) => {
                      const low = isLowStoreOc(store.oc_balance);
                      return (
                        <div key={store.id} className={`store-card${low ? ' store-card--low' : ''}`}>
                          <header>
                            <strong>{store.name}</strong>
                            {low ? <StatusBadge tone="danger">OC 부족</StatusBadge> : null}
                          </header>
                          <div className="store-card__balance">{formatOc(store.oc_balance)}</div>
                          <small className="muted-text">포인트 환율 {formatInteger(store.krw_per_1000_points)}원 / 1,000P</small>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>

            <section className="dashboard-grid">
              <div className="data-panel">
                {panelHeading(<Coins size={18} />, '최근 OC 거래', '본사 기준 최신 5건')}
                <DataTable
                  columns={[
                    { header: '일시', render: (row: FranchiseOcTransaction) => formatDateTime(row.created_at) },
                    { header: '유형', render: (row) => ocTransactionTypeLabel(row.transaction_type) },
                    { header: 'OC', align: 'right', render: (row) => `${row.amount > 0 ? '+' : ''}${formatInteger(row.amount)}` },
                  ]}
                  emptyLabel="최근 OC 거래가 없습니다."
                  rows={data.recent_oc_transactions}
                />
              </div>
              <div className="data-panel">
                {panelHeading(<ClipboardList size={18} />, '최근 OC 요청', '최신 5건')}
                <DataTable
                  columns={[
                    { header: '일시', render: (row: FranchiseOcRequest) => formatDateTime(row.created_at) },
                    { header: 'OC', align: 'right', render: (row) => formatOc(row.amount) },
                    { header: '상태', render: (row) => <StatusBadge tone={OC_REQUEST_STATUS_TONE[row.status] ?? 'neutral'}>{OC_REQUEST_STATUS_LABEL[row.status] ?? row.status}</StatusBadge> },
                  ]}
                  emptyLabel="최근 OC 요청이 없습니다."
                  rows={data.recent_oc_requests}
                />
              </div>
            </section>
          </>
        );
      }}
    </QueryPanel>
  );
}

// ---------------------------------------------------------------- OC

function OcRequestDialog({
  onClose,
  onDone,
  stores,
}: {
  onClose: () => void;
  onDone: (message: string) => void;
  stores: FranchiseStore[];
}) {
  const mutation = useRequestOc();
  const [amountText, setAmountText] = useState('');
  const [krwText, setKrwText] = useState('');
  const [reason, setReason] = useState('');
  const [storeId, setStoreId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (mutation.isPending) return;
    setErrorMessage(null);
    const amount = parsePositiveInteger(amountText);
    if (amount === null) return setErrorMessage('충전 수량은 양의 정수로 입력하세요.');
    const amountKrw = parseOptionalKrw(krwText);
    if (amountKrw === undefined) return setErrorMessage('결제 금액은 0 이상의 숫자로 입력하세요.');
    if (reason.trim().length < 3) return setErrorMessage('요청 사유는 3자 이상 입력하세요.');
    try {
      await mutation.mutateAsync({ amount, amountKrw, reason, storeId: storeId || null });
      const target = stores.find((store) => store.id === storeId)?.name ?? '본사';
      onDone(`${target}에 OC ${formatInteger(amount)}개 충전을 요청했습니다. 시스템 관리자의 승인을 기다립니다.`);
    } catch (error) {
      setErrorMessage(errorMessageOf(error, 'OC 충전을 요청하지 못했습니다.'));
    }
  }

  return (
    <ModalFrame
      title="OC 충전 요청"
      description="시스템 관리자가 승인하면 대상의 OC 잔액에 반영됩니다."
      onClose={mutation.isPending ? () => undefined : onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <FormField label="충전 대상">
          <select value={storeId} onChange={(event) => setStoreId(event.target.value)} disabled={mutation.isPending}>
            <option value="">본사</option>
            {stores.map((store) => (
              <option key={store.id} value={store.id}>
                {store.name} (현재 {formatOc(store.oc_balance)})
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="충전 수량 (OC)">
          <input value={amountText} inputMode="numeric" placeholder="1000" onChange={(event) => setAmountText(event.target.value)} disabled={mutation.isPending} />
        </FormField>
        <FormField label="결제 금액 (원, 선택)">
          <input value={krwText} inputMode="numeric" placeholder="25000" onChange={(event) => setKrwText(event.target.value)} disabled={mutation.isPending} />
        </FormField>
        <FormField label="요청 사유 (3자 이상)">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={200} disabled={mutation.isPending} />
        </FormField>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={mutation.isPending}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? '요청 중' : '충전 요청'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

export function FranchiseOcPage() {
  const ocQuery = useFranchiseOc();
  const storesQuery = useFranchiseStores();
  const cancelMutation = useCancelOcRequest();
  const [requestOpen, setRequestOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<FranchiseOcRequest | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const requestCols = useMemo(() => requestColumns(setCancelTarget), []);
  const txCols = useMemo(() => transactionColumns(), []);

  return (
    <div className="page-stack">
      <PageHeading eyebrow="Franchise Admin" title="OC 상태" description="본사 OC 잔액, 본사·매장 거래 내역, 충전 요청 현황을 확인하고 새 충전을 요청합니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <QueryPanel
        data={ocQuery.data}
        errorLabel="OC 현황을 불러오지 못했습니다."
        isLoading={ocQuery.isLoading}
        onRetry={() => void ocQuery.refetch()}
      >
        {(data) => (
          <>
            <section className="balance-card" aria-label="본사 OC 잔액">
              <div>
                <span>{data.franchise?.name ?? '프랜차이즈'} 본사 OC 잔액</span>
                <strong>{formatOc(data.franchise?.oc_balance ?? 0)}</strong>
                <small>대기 중인 요청 {data.requests.filter((request) => request.status === 'pending').length}건</small>
              </div>
              <button className="secondary-button" type="button" onClick={() => setRequestOpen(true)}>
                <PlusCircle size={16} />
                OC 충전 요청
              </button>
            </section>

            <section className="data-panel">
              {panelHeading(<Send size={18} />, 'OC 요청', '대기 중인 요청은 취소할 수 있습니다')}
              <DataTable columns={requestCols} emptyLabel="OC 요청 내역이 없습니다." rows={data.requests} />
            </section>

            <section className="data-panel">
              {panelHeading(<Coins size={18} />, 'OC 거래 내역', '본사와 소속 매장 · 최신 100건')}
              <DataTable columns={txCols} emptyLabel="OC 거래 내역이 없습니다." rows={data.transactions} />
            </section>
          </>
        )}
      </QueryPanel>

      {requestOpen ? (
        <OcRequestDialog
          stores={storesQuery.data ?? []}
          onClose={() => setRequestOpen(false)}
          onDone={(message) => {
            setRequestOpen(false);
            setNotice(message);
          }}
        />
      ) : null}
      {cancelTarget ? (
        <ConfirmDialog
          title="OC 요청 취소"
          description="대기 중인 요청을 취소합니다. 취소한 요청은 되돌릴 수 없습니다."
          confirmLabel="요청 취소"
          pendingLabel="취소 중"
          danger
          onClose={() => setCancelTarget(null)}
          onConfirm={async () => {
            await cancelMutation.mutateAsync(cancelTarget.id);
            setCancelTarget(null);
            setNotice('OC 요청을 취소했습니다.');
          }}
          rows={[
            ['대상', requestTarget(cancelTarget)],
            ['OC', formatOc(cancelTarget.amount)],
            ['사유', cancelTarget.reason],
          ]}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- 매장 비교 + OC 배분/회수

function AllocationDialog({
  franchiseBalance,
  onClose,
  onDone,
  store,
}: {
  franchiseBalance: number;
  onClose: () => void;
  onDone: (message: string) => void;
  store: FranchiseStore;
}) {
  const mutation = useAllocateOc();
  const [mode, setMode] = useState<AllocationMode>('allocate');
  const [amountText, setAmountText] = useState('');
  const [reason, setReason] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const amount = parsePositiveInteger(amountText);
  const preview = amount === null ? null : previewAllocation(franchiseBalance, store.oc_balance, mode, amount);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (mutation.isPending) return;
    setErrorMessage(null);
    if (amount === null) return setErrorMessage('수량은 양의 정수로 입력하세요.');
    if (preview?.error) return setErrorMessage(preview.error);
    if (reason.trim().length < 3) return setErrorMessage('사유는 3자 이상 입력하세요.');
    try {
      const result = await mutation.mutateAsync({ storeId: store.id, amount: mode === 'allocate' ? amount : -amount, reason });
      onDone(
        `${store.name}에서 OC ${formatInteger(amount)}개를 ${mode === 'allocate' ? '배분' : '회수'}했습니다. (본사 ${formatOc(result.franchise_balance)}, 매장 ${formatOc(result.store_balance)})`,
      );
    } catch (error) {
      setErrorMessage(errorMessageOf(error, 'OC를 처리하지 못했습니다.'));
    }
  }

  return (
    <ModalFrame
      title={`OC 배분/회수 · ${store.name}`}
      description="배분은 본사 잔액에서 매장으로, 회수는 매장 잔액에서 본사로 옮깁니다."
      onClose={mutation.isPending ? () => undefined : onClose}
    >
      <form className="modal-form" onSubmit={(event) => void handleSubmit(event)}>
        <div className="segmented" role="group" aria-label="작업 유형">
          {(['allocate', 'recall'] as const).map((value) => (
            <button key={value} type="button" aria-pressed={mode === value} onClick={() => setMode(value)} disabled={mutation.isPending}>
              {value === 'allocate' ? '배분 (본사 → 매장)' : '회수 (매장 → 본사)'}
            </button>
          ))}
        </div>
        <FormField label="수량 (OC)">
          <input value={amountText} inputMode="numeric" placeholder="100" onChange={(event) => setAmountText(event.target.value)} disabled={mutation.isPending} />
        </FormField>
        <dl className="summary-list">
          <dt>본사 OC</dt>
          <dd>
            {formatOc(franchiseBalance)}
            {preview ? ` → ${formatOc(preview.franchiseBalance)}` : ''}
          </dd>
          <dt>{store.name} OC</dt>
          <dd>
            {formatOc(store.oc_balance)}
            {preview ? ` → ${formatOc(preview.storeBalance)}` : ''}
          </dd>
        </dl>
        {preview?.error ? <ActionNotice tone="warning">{preview.error}</ActionNotice> : null}
        <FormField label="사유 (3자 이상)">
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={200} disabled={mutation.isPending} />
        </FormField>
        <FormHelp>처리 결과의 최종 잔액은 서버 응답 기준으로 표시됩니다.</FormHelp>
        {errorMessage ? <ActionNotice tone="danger">{errorMessage}</ActionNotice> : null}
        <div className="modal-actions">
          <button className="secondary-button" type="button" onClick={onClose} disabled={mutation.isPending}>
            취소
          </button>
          <button className="primary-button" type="submit" disabled={mutation.isPending || !!preview?.error}>
            {mutation.isPending ? '처리 중' : mode === 'allocate' ? '배분' : '회수'}
          </button>
        </div>
      </form>
    </ModalFrame>
  );
}

export function FranchiseStoresPage() {
  const storesQuery = useFranchiseStores();
  const dashboardQuery = useFranchiseDashboard();
  const [target, setTarget] = useState<FranchiseStore | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const franchiseBalance = dashboardQuery.data?.franchise.oc_balance;

  const columns = useMemo<DataColumn<FranchiseStore>[]>(
    () => [
      { header: '매장', render: (row) => row.name },
      {
        header: 'OC 잔액',
        align: 'right',
        render: (row) => (
          <>
            {formatOc(row.oc_balance)} {isLowStoreOc(row.oc_balance) ? <StatusBadge tone="danger">부족</StatusBadge> : null}
          </>
        ),
      },
      { header: '포인트 환율(원/1,000P)', align: 'right', render: (row) => formatInteger(row.krw_per_1000_points) },
      { header: '생성일', render: (row) => formatDateTime(row.created_at) },
      {
        header: '작업',
        render: (row) => (
          <button className="secondary-button compact-button" type="button" onClick={() => setTarget(row)} disabled={franchiseBalance === undefined}>
            <Coins size={14} />
            OC 배분/회수
          </button>
        ),
      },
    ],
    [franchiseBalance],
  );

  return (
    <div className="page-stack">
      <PageHeading eyebrow="Franchise Admin" title="매장 비교" description="매장별 OC 잔액을 비교하고 본사 OC를 매장에 배분하거나 회수합니다." />
      {notice ? <ActionNotice>{notice}</ActionNotice> : null}
      <div className="action-toolbar">
        <Coins size={17} />
        <span>
          본사 OC 잔액 <strong>{franchiseBalance === undefined ? '-' : formatOc(franchiseBalance)}</strong>
        </span>
        <span>{LOW_STORE_OC_THRESHOLD} OC 이하인 매장은 부족으로 표시됩니다.</span>
      </div>
      <section className="data-panel">
        <QueryPanel
          data={storesQuery.data}
          errorLabel="매장 목록을 불러오지 못했습니다."
          isLoading={storesQuery.isLoading}
          onRetry={() => void storesQuery.refetch()}
        >
          {(rows) => <DataTable columns={columns} emptyLabel="조회 가능한 매장이 없습니다." rows={rows} />}
        </QueryPanel>
      </section>
      {target && franchiseBalance !== undefined ? (
        <AllocationDialog
          key={target.id}
          franchiseBalance={franchiseBalance}
          store={storesQuery.data?.find((store) => store.id === target.id) ?? target}
          onClose={() => setTarget(null)}
          onDone={(message) => {
            setTarget(null);
            setNotice(message);
          }}
        />
      ) : null}
    </div>
  );
}
